<template>
    <PageWrapper main-class="max-w-9xl">
        <template #headline>
            <fa icon="fa-solid fa-rocket" />&nbsp;{{ campaign.asset_title }}
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="
                    campaign.asset_var_uid &&
                    $router.options.history.state.back !==
                        `/campaigns${campaign.asset_var_uid}`
                "
                icon="fa-eye"
                title="campaign"
                @click="goToDetails"
            />
            <BackButton
                v-if="$router.options.history.state.back !== '/campaigns'"
                icon="fa-list"
                title="campaigns"
                @click="toCampaigns"
            />
            <SaveButton @click="saveCampaign" />
        </template>

        <AccordionBasic
            title="campaign_details"
            class="mb-2"
            :open="true"
        >
            <TextField
                v-model="campaign.asset_title"
                label="campaign"
                name="campaign"
                :required="true"
                help="help_select_campaign"
            />
            <TextField
                v-model="campaign.slug"
                label="slug"
                name="slug"
                :required="true"
                help="help_campaign_slug"
            />
            <TextField
                v-model="campaign.asset_subtitle"
                label="campaign_subtitle"
                name="campaign_subtitle"
                :required="true"
                help="help_campaign_subtitle"
            />
            <TextField
                v-model="campaign.asset_summary"
                label="campaign_summary"
                name="campaign_summary"
                :rows="4"
                help="help_campaign_summary"
            />
            <EditorField
                v-model="campaign.campaign_additional_info"
                label="campaign_additional_info"
                name="additional_info"
                :rows="4"
                help="help_campaign_additional_info"
            />
            <SelectField
                v-model="campaign.platform_name"
                v-model:options="platformSelectOptions"
                name="platform_name"
                label="platform"
                help="help_platform_name_campaign"
                :on-search="loadPlatformSelectOptions"
                :required="true"
            />
            <SelectField
                v-model="campaign.asset_uid"
                v-model:options="assetSelectOptions"
                name="asset_title"
                label="asset"
                help="help_asset_title_campaign"
                :on-search="loadAssetSelectOptions"
                :required="true"
            />
            <TextField
                v-model="campaign.asset_excerpt"
                label="campaign_excerpt"
                name="campaign_excerpt"
                :rows="4"
                help="help_campaign_excerpt"
            />
            <TextField
                v-model="campaign.external_project_url"
                label="external_project_url"
                name="external_project_url"
                :required="false"
                help="help_external_project_url"
            />
            <p class="mt-6 text-base font-bold text-gray-800">
                {{ $t('country_and_language') }}
            </p>
            <div
                class="flex h-auto w-full flex-col items-start justify-start md:flex-row md:items-center md:space-x-8"
            >
                <SelectField
                    v-model="campaign.country"
                    name="country"
                    label="country"
                    help="help_country_campaign"
                    :options="countries"
                />
                <SelectField
                    v-model="campaign.language"
                    name="language"
                    label="language"
                    help="help_language_campaign"
                    :options="languages"
                    :required="true"
                />
                <SelectField
                    v-model="campaign.number_locale"
                    name="number_locale"
                    label="number_locale"
                    help="help_number_locale_campaign"
                    :options="numberLocaleOptions"
                />
            </div>
            <p class="text-base font-bold text-gray-800">
                {{ $t('campaign_status') }}
            </p>
            <SelectField
                v-model="campaign.status"
                name="status"
                label="status"
                help="help_status_campaign"
                :options="mapEnumToArray(CampaignStatus)"
                :reduce="reduceEnumValue"
                :required="true"
            />
            <TextField
                v-if="campaign.status === CampaignStatus.PRIVATE_PLACEMENT"
                v-model="campaign.asset_var_password"
                label="password"
                name="password"
                :required="true"
                type="password"
                help="help_password_campaign"
            />
            <ToggleSwitch
                v-model="campaign.funding_stats_visible"
                name="funding_stats_visible"
                label="funding_stats_visible"
                help="help_funding_stats_visible"
            />
            <ToggleSwitch
                v-model="campaign.is_co_placement"
                name="is_co_placement"
                label="is_co_placement"
                help="help_is_co_placement"
            />
            <p class="mt-6 text-base font-bold text-gray-800">
                {{ $t('funding_period') }}
            </p>
            <div
                class="flex h-auto w-full flex-col items-center justify-start md:flex-row md:space-x-8"
            >
                <Datepicker
                    v-model="campaign.funding_start"
                    label="funding_start"
                    name="funding_start"
                    help="help_funding_start"
                    date-format="Y-m-d H:i"
                    :enable-time="true"
                    :bottom-help-text="
                        getDatePickerTextHint('bottom_help_funding_start')
                    "
                />
                <Datepicker
                    v-model="campaign.funding_end"
                    label="funding_end"
                    name="funding_end"
                    help="help_funding_end"
                    date-format="Y-m-d H:i"
                    :enable-time="true"
                    :bottom-help-text="
                        getDatePickerTextHint('bottom_help_funding_end')
                    "
                />
            </div>
            <TextField
                v-model="campaign.waitlist_url"
                label="waitlist_url"
                name="waitlist_url"
                help="help_waitlist_url"
                :required="false"
            />
            <EditorField
                v-model="campaign.waitlist_text"
                name="waitlist_text"
                label="waitlist_text"
                help="help_waitlist_text"
                variant="minimal"
                :required="false"
            />
            <p class="text-base font-bold text-gray-800">
                {{ $t('secondary_market_settings') }}
            </p>
            <ToggleSwitch
                v-model="campaign.sm_pre_emptive_rights_required"
                name="sm_pre_emptive_rights"
                label="sm_pre_emptive_rights"
                help="help_sm_pre_emptive_rights"
            />
        </AccordionBasic>
        <AccordionBasic
            title="campaign_settings"
            help="help_campaign_settings"
            class="mb-2"
            :open="false"
        >
            <SelectField
                v-if="campaign.agio_disagio_feature_active"
                v-model="campaign.agio_disagio"
                name="agio_disagio"
                label="agio_disagio"
                help="help_agio_disagio"
                :options="mapEnumToArray(AgioDisagio)"
                :reduce="reduceEnumValue"
                :required="true"
            />
            <SelectField
                v-model="campaign.regulatory_requirements"
                name="regulatory_requirements"
                label="regulatory_requirements"
                help="help_regulatory_requirements"
                :options="mapEnumToArray(RegulatoryRequirements)"
                :reduce="reduceEnumValue"
                :required="true"
            />
            <SelectField
                v-model="campaign.asset_regulation_override"
                name="asset_regulation_override"
                label="asset_regulation_override"
                help="help_asset_regulation_override"
                :options="mapEnumToArray(AssetRegulation)"
                :reduce="reduceEnumValue"
                :required="false"
            />
            <ToggleSwitch
                v-model="broker.broker_status"
                name="broker_status"
                label="broker_status"
                help="help_broker_status"
            />
            <ToggleSwitch
                v-if="
                    asset.clearing_system === ClearingSystem['EMISSION'] ||
                    asset.clearing_system === ClearingSystem['STOCK_EMISSION']
                "
                v-model="campaign.additional_fields_enabled"
                name="additional_fields_enabled"
                label="additional_fields"
                help="help_additional_fields_enabled"
                @update:model-value="activateAdditionalFields"
            />
            <ToggleSwitch
                v-model="campaign.savings_plan"
                name="savings_plan"
                label="savings_plan"
                help="help_savings_plan"
            />
            <ToggleSwitch
                v-model="campaign.disable_invest_button"
                name="disable_invest_button"
                label="disable_campaigns_button"
                help="help_disable_campaigns_button"
            />
        </AccordionBasic>
        <AccordionBasic
            title="appearance_and_content"
            class="mb-2"
            :open="false"
            :lazy="true"
        >
            <p class="text-base font-bold text-gray-800">
                {{ $t('appearance') }}
            </p>
            <SelectField
                v-model="campaign.badge"
                name="campaign_badge"
                label="campaign_badge"
                help="help_campaign_badge"
                :options="mapEnumToArray(CampaignBadge)"
                :reduce="reduceEnumValue"
            />
            <ToggleSwitch
                v-model="campaign.show_amount_slider"
                name="show_amount_slider"
                label="show_amount_slider"
                help="help_show_amount_slider"
            />
            <ImageUpload
                v-model="campaign.thumb_image"
                name="campaign_thumb_image"
                label="campaign_thumb_image"
                help="help_campaign_thumb_image"
                type="campaign"
            />
            <ImageUpload
                v-model="campaign.hero_image"
                name="campaign_hero_image"
                label="campaign_hero_image"
                help="help_campaign_hero_image"
                type="campaign"
            />
            <TextField
                v-model="campaign.asset_video"
                label="asset_video"
                name="asset_video"
                :required="false"
                help="help_asset_video"
            />

            <p class="mt-4 text-base font-bold text-gray-800">
                {{ $t('content') }}
            </p>
            <EditorField
                v-if="campaign.asset_var_uid"
                v-model="campaign.asset_description"
                v-model:placeholders="placeholders"
                v-model:document-placeholders="documentPlaceholders"
                name="asset_description"
                label="main"
                help="help_main_campaign"
                :required="false"
            />
            <EditorField
                v-if="campaign.asset_var_uid"
                v-model="campaign.investment_summary"
                v-model:placeholders="placeholders"
                v-model:document-placeholders="documentPlaceholders"
                name="investment_summary"
                label="summary"
                help="help_summary_campaign"
                :required="false"
            />
        </AccordionBasic>
        <AccordionBasic
            title="seo"
            class="mb-2"
            :open="false"
            :lazy="true"
        >
            <TextField
                v-model="campaign.og_title"
                name="og_title"
                label="og_title"
                help="help_campaign_og_title"
                :required="false"
            />
            <TextField
                v-model="campaign.og_description"
                name="og_description"
                label="og_description"
                help="help_campaign_og_description"
                :required="false"
            />
            <TextField
                v-model="campaign.focus_keyword"
                name="focus_keyword"
                label="focus_keyword"
                :required="false"
            />
            <div class="w-full">
                <label class="mb-1 block text-sm font-medium">{{
                    $t('og_image')
                }}</label>
                <div
                    v-if="campaign.og_image"
                    class="mb-2 space-y-2"
                >
                    <div
                        class="relative w-full overflow-hidden rounded-lg bg-slate-100"
                        style="aspect-ratio: 16/9"
                    >
                        <img
                            :src="campaign.og_image.asset.url"
                            :alt="campaign.og_image.alt"
                            class="h-full w-full object-contain"
                        />
                        <div class="absolute right-2 top-2 flex gap-1">
                            <button
                                type="button"
                                class="rounded bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                                @click="ogImageUploadRef?.openModal()"
                            >
                                {{ $t('update') }}
                            </button>
                            <button
                                type="button"
                                class="rounded bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                                @click="
                                    ;(campaign.og_image = null) && ogImageKey++
                                "
                            >
                                {{ $t('delete') }}
                            </button>
                        </div>
                    </div>
                    <input
                        v-model="campaign.og_image.alt"
                        type="text"
                        class="form-input w-full"
                        :placeholder="$t('alt_text')"
                    />
                </div>
                <div v-show="!campaign.og_image">
                    <FileUploadForm
                        :key="ogImageKey"
                        ref="ogImageUploadRef"
                        @select="onOgImageSelect"
                    />
                </div>
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="broker.broker_status"
            title="broker"
            class="mb-2"
            :open="false"
        >
            <ToggleSwitch
                v-if="broker.broker_status"
                v-model="broker.broker_field_required"
                name="broker_field_required"
                label="required_field"
                help="help_broker_field_required"
            />
            <TextField
                v-if="broker.broker_status"
                v-model="broker.broker_disclaimer"
                label="broker_disclaimer"
                name="broker_disclaimer"
                help="help_broker_disclaimer"
            />
        </AccordionBasic>

        <AccordionBasic
            v-if="
                (asset.clearing_system === ClearingSystem['EMISSION'] ||
                    asset.clearing_system ===
                        ClearingSystem['STOCK_EMISSION']) &&
                campaign.additional_fields_enabled
            "
            title="additional_fields"
            class="mb-2"
            :help="$t('help_additional_fields')"
            :open="false"
        >
            <div
                v-if="campaign.additional_fields"
                class="mt-4"
            >
                <ToggleSwitch
                    v-model="campaign.additional_fields.depot_owner.required"
                    label="depot_owner"
                    name="depot_owner"
                />
                <ToggleSwitch
                    v-model="campaign.additional_fields.depot_number.required"
                    label="depot_number"
                    name="depot_number"
                />
                <ToggleSwitch
                    v-model="campaign.additional_fields.depot_bank.required"
                    label="depot_bank"
                    name="depot_bank"
                />
                <ToggleSwitch
                    v-model="campaign.additional_fields.depot_bic.required"
                    label="depot_bic"
                    name="depot_bic"
                />
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="campaign.agio_disagio"
            title="agio_disagio"
            class="mb-2"
            :open="false"
        >
            <div v-if="campaign.agio_disagio !== AgioDisagio['DEACTIVATED']">
                <ToggleSwitch
                    v-model="campaign.display_price_without_agio"
                    name="display_price_without_agio"
                    label="display_price_without_agio"
                    help="help_display_price_without_agio"
                />
                <TextField
                    v-model="campaign.agio_disagio_title"
                    label="agio_disagio_title"
                    name="agio_disagio_title"
                    :rows="4"
                    help="help_agio_disagio_title"
                />
                <NumberField
                    v-model="campaign.agio_disagio_rate"
                    label="agio_disagio_rate"
                    name="agio_disagio_rate"
                    help="help_agio_disagio_rate"
                />
                <SelectField
                    v-model="campaign.agio_disagio_type"
                    name="agio_disagio_type"
                    label="agio_disagio_type"
                    help="help_agio_disagio_type"
                    :options="mapEnumToArray(AgioDisagioType)"
                    :reduce="reduceEnumValue"
                    :required="true"
                />
                <TextField
                    v-model="campaign.agio_disagio_info_text"
                    label="agio_disagio_info_text"
                    name="agio_disagio_info_text"
                    :rows="4"
                    help="help_agio_disagio_info_text"
                />
                <TextField
                    v-model="campaign.agio_disagio_disclaimer"
                    label="agio_disagio_disclaimer"
                    name="agio_disagio_disclaimer"
                    :rows="4"
                    help="help_agio_disagio_disclaimer"
                />
                <ToggleSwitch
                    v-model="agioDisagioVoucher"
                    name="agio_disagio_voucher"
                    label="agio_disagio_voucher"
                    help="help_agio_disagio_voucher"
                />
                <div v-if="agioDisagioVoucher">
                    <TextField
                        v-model="campaign.voucher_code"
                        label="voucher_code"
                        name="voucher_code"
                        help="help_voucher_code"
                    />
                    <NumberField
                        v-model="campaign.voucher_rate"
                        label="voucher_rate"
                        name="voucher_rate"
                        help="help_voucher_rate"
                    />
                </div>
            </div>
        </AccordionBasic>
        <AccordionBasic
            v-if="campaign.savings_plan"
            title="savings_plan"
            class="mb-2"
            :open="false"
        >
            <TextField
                v-model="campaign.savings_plan_description"
                :disabled="!campaign.savings_plan"
                rows="3"
                label="savings_plan_description"
                name="savings_plan_description"
                help="help_savings_plan_description"
            />
        </AccordionBasic>
        <AccordionBasic
            title="marketing_content"
            class="mb-2"
            :open="false"
        >
            <CampaignMarketingContentForm
                v-if="campaign"
                v-model:campaign="campaign"
            >
                <template #incentives>
                    <CampaignIncentivesTable>
                        <template #actions>
                            <CreateButton
                                v-if="userStore.can('update_campaign')"
                                @click.stop="
                                    () => handleEditOrCreateIncentive()
                                "
                            />
                            <DeleteBulkButton
                                v-if="userStore.can('update_campaign')"
                                v-model:selected="selectedIncentivesIds"
                                @click.stop="() => handleDeleteIncentives()"
                            />
                        </template>
                    </CampaignIncentivesTable>
                </template>
                <template #team_members>
                    <CampaignTeamMembersTable>
                        <template #actions>
                            <CreateButton
                                v-if="userStore.can('update_campaign')"
                                @click.stop="
                                    () => handleEditOrCreateTeamMember()
                                "
                            />
                            <DeleteBulkButton
                                v-if="userStore.can('update_campaign')"
                                v-model:selected="selectedTeamMembersIds"
                                @click.stop="() => handleDeleteTeamMembers()"
                            />
                        </template>
                    </CampaignTeamMembersTable>
                </template>
            </CampaignMarketingContentForm>
        </AccordionBasic>
        <ModalWrapper v-if="isModalOpen" />
    </PageWrapper>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
    AccordionBasic,
    Datepicker,
    EditorField,
    NumberField,
    SelectField,
    TextField,
    ToggleSwitch,
} from '@src/components'
import BackButton from '@src/partials/actions/BackButton.vue'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import DeleteBulkButton from '@src/partials/actions/DeleteBulkButton.vue'
import SaveButton from '@src/partials/actions/SaveButton.vue'
import PageWrapper from '@src/partials/pages/PageWrapper.vue'

import { useEnum, useModal } from '@composables/common'
import { useCampaignIncentivesTable } from '@composables/campaigns/useCampaignIncentivesTable'
import { useCampaignTeamMembersTable } from '@composables/campaigns/useCampaignTeamMembersTable'
import ImageUpload from '@src/components/ImageUpload.vue'
import FileUploadForm from '@src/partials/pageBuilder/FileUploadForm.vue'
import type { MediaAsset } from '@builder/types'
import useEditLock from '@src/composables/editLock'
import {
    AgioDisagio,
    AgioDisagioType,
    AssetRegulation,
    CampaignBadge,
    CampaignStatus,
    ClearingSystem,
    RegulatoryRequirements,
} from '@src/enums'
import {
    Asset,
    AssetsIncentive,
    Broker,
    Campaign,
    CampaignAdditionalFields,
    CampaignImage,
    ContentDocument,
    Placeholder,
    Platform,
    TeamMember,
} from '@src/types'
import { getAsset, getAssets } from '@src/utils/api/assets'
import { getCampaign, updateCampaign } from '@src/utils/api/campaigns'
import { getPlatforms } from '@src/utils/api/platforms'
import { useCountryStore } from '@src/utils/stores/countries'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useToastStore } from '@src/utils/stores/toast'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { useI18n } from 'vue-i18n'
import CampaignMarketingContentForm from '@src/partials/campaigns/CampaignMarketingContentForm.vue'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()
const { mapEnumToArray, reduceEnumValue } = useEnum()
const userStore = useUserStore()
const { ModalWrapper, openModal } = useModal()

useEditLock('campaign', route.params.id as string)

const isModalOpen = ref(false)
const campaign = ref<Campaign>(new Campaign())
const asset = ref<Asset>(new Asset())
const ogImageUploadRef = ref<InstanceType<typeof FileUploadForm> | null>(null)
const ogImageKey = ref(0)
const onOgImageSelect = (mediaAsset: MediaAsset) => {
    campaign.value.og_image = { asset: mediaAsset, alt: '' }
}
const incentives = ref<AssetsIncentive[]>([])
const teamMembers = ref<TeamMember[]>([])
const assetCurrency = ref<string | null>(null)
const languageStore = useLanguageStore()
const languages = ref<{ label: string; value: string | null }[]>([])
const numberLocaleOptions = [
    { label: 'en-US  (1,500.00)', value: 'en-US' },
    { label: 'de-DE  (1.500,00)', value: 'de-DE' },
    { label: 'fr-FR  (1 500,00)', value: 'fr-FR' },
    { label: 'it-IT  (1.500,00)', value: 'it-IT' },
    { label: 'es-ES  (1.500,00)', value: 'es-ES' },
    { label: 'sr-RS  (1.500,00)', value: 'sr-RS' },
]
const countryStore = useCountryStore()
const countries = ref<{ label: string; value: string | null }[]>([])
const broker = ref<Broker>(new Broker())
const agioDisagioVoucher = ref<boolean>(false)

const platformSelectOptions = ref<string[]>([])
const assetSelectOptions = ref<{ label: string; value: string | null }[]>([])

const {
    CampaignIncentivesTable,
    selectedIncentivesIds,
    handleEditOrCreateIncentive,
    handleDeleteIncentives,
} = useCampaignIncentivesTable(
    incentives,
    assetCurrency,
    updateIncentives,
    openModal,
    isModalOpen
)

const {
    CampaignTeamMembersTable,
    selectedTeamMembersIds,
    handleDeleteTeamMembers,
    handleEditOrCreateTeamMember,
} = useCampaignTeamMembersTable(
    teamMembers,
    updateTeamMembers,
    openModal,
    isModalOpen
)

const placeholders = computed((): Placeholder[] => {
    return campaign.value.content_images.map((image: CampaignImage) => {
        return {
            key: image.name,
            value: `<img src="${image.thumb_image_url}" alt="${image.name}" />`,
        } as Placeholder
    })
})

const documentPlaceholders = computed((): Placeholder[] => {
    return campaign.value.content_documents.map((doc: ContentDocument) => {
        return {
            key: doc.name,
            value: `<a href="${doc.url}" target="_blank">${doc.name}</a>`,
        } as Placeholder
    })
})

async function loadPlatformSelectOptions(search: string) {
    const initialPlatformsRequest = await getPlatforms({
        'filter[platform_name]': search,
    })
    platformSelectOptions.value = initialPlatformsRequest.data.platforms.map(
        (platform: Platform) => {
            return platform.platform_name as string
        }
    )
}

async function loadAssetSelectOptions(search: string) {
    const assetsRequest = await getAssets({
        'filter[asset_title]': search,
    })
    assetSelectOptions.value = assetsRequest.data.assets.map((asset: Asset) => {
        return {
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        }
    })
}

function activateAdditionalFields() {
    if (
        campaign.value.additional_fields_enabled &&
        !campaign.value.additional_fields
    ) {
        campaign.value.additional_fields = new CampaignAdditionalFields()
    } else {
        campaign.value.additional_fields = null
    }
}

function goToDetails() {
    router.push({
        name: 'campaign',
        params: { id: campaign.value.asset_var_uid },
    })
}

function toCampaigns() {
    router.push({ name: 'campaigns' })
}

async function updateIncentives(
    successMessage: string | null = null
): Promise<void> {
    campaign.value.assets_incentives.sort((a, b) => a.position - b.position)
    const updateCampaignResponse = await updateCampaign(
        campaign.value,
        campaign.value.asset_var_uid as string
    )
    if (updateCampaignResponse.data.success && successMessage) {
        toast.success(successMessage)
    }
    campaign.value.slug = updateCampaignResponse.data.campaign.slug
    campaign.value.assets_incentives =
        updateCampaignResponse.data.campaign.assets_incentives
    incentives.value = campaign.value.assets_incentives
}

async function updateTeamMembers(
    successMessage: string | null = null
): Promise<void> {
    campaign.value.team_members.sort((a, b) => a.position - b.position)
    const updateCampaignResponse = await updateCampaign(
        campaign.value,
        campaign.value.asset_var_uid as string
    )
    if (updateCampaignResponse.data.success && successMessage) {
        toast.success(successMessage)
    }
    campaign.value.slug = updateCampaignResponse.data.campaign.slug
    campaign.value.team_members =
        updateCampaignResponse.data.campaign.team_members
    teamMembers.value = campaign.value.team_members
}

async function saveCampaign() {
    // A Private Placement is only shown with its password; the API refuses it without one too.
    if (
        campaign.value.status === CampaignStatus.PRIVATE_PLACEMENT &&
        !campaign.value.asset_var_password?.trim()
    ) {
        toast.error(i18n.t('error_private_placement_password_required'))
        return
    }

    campaign.value.broker = broker.value

    const updateCampaignResponse = await updateCampaign(
        campaign.value,
        campaign.value.asset_var_uid as string
    )
    // A new title gives a new slug on the api; take it over, or the next save would send the
    // old one back and undo it.
    campaign.value.slug = updateCampaignResponse.data.campaign.slug
    toast.success(
        i18n.t('toast_success_campaign_saved', {
            asset: campaign.value.asset_title,
        })
    )
    if (campaign.value.hero_image) {
        campaign.value.hero_image.upload_key = null
    }
    if (campaign.value.thumb_image) {
        campaign.value.thumb_image.upload_key = null
    }
    if (campaign.value.esg_document) {
        // The api removes the temp upload as soon as the media is stored, so the temp url
        // the form still holds points at a deleted file. Take the stored document back
        // from the response instead of leaving a dead download link behind.
        const savedDocument = updateCampaignResponse.data.campaign.esg_document
        campaign.value.esg_document.upload_key = null
        campaign.value.esg_document.id = savedDocument?.id ?? null
        campaign.value.esg_document.file_name = savedDocument?.file_name ?? null
        campaign.value.esg_document.name = savedDocument?.file_name ?? null
        campaign.value.esg_document.url = savedDocument?.url ?? null
        delete campaign.value.esg_document.delete
    }
}

function getDatePickerTextHint(text: string) {
    let utcOffsetText = `${Math.floor(moment().utcOffset() / 60)} ${i18n.t(
        'hours'
    )} ${Math.floor(moment().utcOffset() % 60)} ${i18n.t('minutes')}`
    utcOffsetText += ` ${i18n.t(
        moment().utcOffset() >= 0
            ? 'bottom_help_funding_ahead'
            : 'bottom_help_funding_behind'
    )}`
    return i18n.t(text, { utcOffset: utcOffsetText })
}

onMounted(async () => {
    const id = route.params.id as string
    if (id) {
        const campaignRequest = await getCampaign(id)
        campaign.value = campaignRequest.data.campaign
    }

    const assetRequest = await getAsset(campaign.value.asset_uid as string)
    asset.value = assetRequest.data.asset
    assetCurrency.value = asset.value.currency
    incentives.value = campaign.value.assets_incentives
    teamMembers.value = campaign.value.team_members

    loadData()

    if (campaign.value.platform_name) {
        await loadPlatformSelectOptions(campaign.value.platform_name)
    }

    if (asset.value.asset_title) {
        await loadAssetSelectOptions(asset.value.asset_title)
    }

    if (campaign.value) {
        broker.value = campaign.value.broker ?? new Broker()
    }

    if (
        campaign.value.additional_fields_enabled &&
        !campaign.value.additional_fields
    ) {
        campaign.value.additional_fields = new CampaignAdditionalFields()
    }
})

async function loadData() {
    countries.value = await countryStore.loadAndSortCountriesOptionsAsync()
    languages.value = await languageStore.loadAndSortLanguagesOptionsAsync()
}

watch(i18n.locale, async () => {
    loadData()
})
</script>
